import React from 'react'
import Markdown from "react-markdown"
import remarkGfm from 'remark-gfm'
import styles from '../style/styles/ChatBubble.module.css';

const CustomUl = (props) => {
    // ** bold 변환 처리
    const children = Array.isArray(props.children) ? props.children : [props.children];

    const processedChildren = children.map((child, index) => {
        if (typeof child === 'string' && (child.match(/\*\*/g) || []).length >= 2) {
            const splitList = child.split('**');
            return splitList.map((segment, segmentIndex) => {
                if (segmentIndex % 2 === 0) {
                    return segment;
                } else {
                    return <strong key={`ul-${index}-${segmentIndex}`}>{segment}</strong>;
                }
            }).filter(Boolean); // Remove empty strings
        }
        // Handle React elements and other non-string content properly
        if (child && typeof child === 'object' && child.type) {
            return child;
        }
        // Handle string content
        if (typeof child === 'string') {
            return child;
        }
        // For any other type, convert to string safely
        return String(child || '');
    }).flat().filter(segment => segment !== '' && segment != null);

    return (
        <ul
            className="h3 fc-gray-800"
            style={{
                listStyleType: 'disc',
                paddingLeft: '0',
                whiteSpace: 'normal'
            }}
            {...props}
        >
            {processedChildren.map((child, index) =>
                typeof child === 'string' ? child : React.cloneElement(child, { key: `ul-child-${index}` })
            )}
        </ul>
    );
};

const CustomLi = (props) => {
    // ** bold 변환 처리
    const children = Array.isArray(props.children) ? props.children : [props.children];

    const processedChildren = children.map((child, index) => {
        if (typeof child === 'string' && (child.match(/\*\*/g) || []).length >= 2) {
            const splitList = child.split('**');
            return splitList.map((segment, segmentIndex) => {
                if (segmentIndex % 2 === 0) {
                    return segment;
                } else {
                    return <strong key={`li-${index}-${segmentIndex}`}>{segment}</strong>;
                }
            }).filter(Boolean); // Remove empty strings
        }
        // Handle React elements and other non-string content properly
        if (child && typeof child === 'object' && child.type) {
            return child;
        }
        // Handle string content
        if (typeof child === 'string') {
            return child;
        }
        // For any other type, convert to string safely
        return String(child || '');
    }).flat().filter(segment => segment !== '' && segment != null);

    return (
        <li className="h3 fc-gray-800" style={{ marginLeft: '20px', marginBottom: '0' }} {...props}>
            {processedChildren.map((child, index) =>
                typeof child === 'string' ? child : React.cloneElement(child, { key: `li-child-${index}` })
            )}
        </li>
    );
};

const CustomP = (props) => {
    // ** bold 변환 처리
    const children = Array.isArray(props.children) ? props.children : [props.children];
    const processedChildren = children.map((child, index) => {
        if (typeof child === 'string' && (child.match(/\*\*/g) || []).length >= 2) {
            const splitList = child.split('**');
            return splitList.map((segment, segmentIndex) => {
                if (segmentIndex % 2 === 0) {
                    return segment;
                } else {
                    return <strong key={`p-${index}-${segmentIndex}`}>{segment}</strong>;
                }
            }).filter(Boolean); // Remove empty strings
        }

        // Handle React elements and other non-string content properly
        if (child && typeof child === 'object' && child.type) {
            return child;
        }
        // Handle string content
        if (typeof child === 'string') {
            return child;
        }
        // For any other type, convert to string safely
        return String(child || '');
    }).flat().filter(segment => segment !== '' && segment != null);

    return (
        <p className="h3 fc-gray-800" style={{ whiteSpace: 'normal' }} {...props}>
            {processedChildren.map((child, index) =>
                typeof child === 'string' ? child : React.cloneElement(child, { key: `p-child-${index}` })
            )}
        </p>
    );
};

const CustomA = (props) => {
    // 포맷: [**[:link: text]**](url) or [**[🔗 text]**](url)
    // Ensure children is always an array
    const children = Array.isArray(props.children) ? props.children : [props.children];

    const isSpecialLink = children &&
        children.some(child => {
            if (typeof child === 'string' && (child.includes(':link:') || child.includes('🔗'))) {
                return true;
            }
            // Or check if it's a React element (like <strong>) that contains :link: or 🔗
            if (child && child.props && child.props.children) {
                const strongChildren = Array.isArray(child.props.children)
                    ? child.props.children
                    : [child.props.children];

                return strongChildren.some(
                    strongChild => typeof strongChild === 'string' &&
                        (strongChild.includes(':link:') || strongChild.includes('🔗'))
                );
            }
            return false;
        });

    if (isSpecialLink) {
        return (
            <a
                {...props}
                target="_blank"
                rel="noopener noreferrer"
                className="special-link h3 fc-gray-800"
            />
        );
    } else {
        return (
            <a
                {...props}
                disabled
                style={{ cursor: 'default' }}
                className="h3 fc-gray-800"
            />
        );
    }
}

// 취소선 처리 무시
const CustomDel = (props) => <>{'~' + props.children + '~'}</>;

const MarkDownConverter = (props) => {
    const { text } = props;
    let processedText = text;
    
    // child가 string이고 --- MD divider 구문이 있을 때는 --- 바로 뒤에 줄바꿈이 없다면 추가하는 로직
    if (typeof text === 'string' && text.includes('---')) {
        // Split at all '---'
        const segments = text.split(/(---)/g);
        const result = segments.map((segment, segIdx) => {
            if (segment === '---') {
                // If next string chunk doesn't start with a \n, add it
                const prev = segments[segIdx - 1];
                if (typeof prev === 'string' && !prev.endsWith('\n\n')) {
                    return ['\n\n', segment];
                }
                return segment;
            }
            return segment;
        }).flat().filter(Boolean);
        processedText = result.join('');
    }
    
    return (
        <Markdown
            components={{
                ul: CustomUl,
                li: CustomLi,
                p: CustomP,
                a: CustomA,
                del: CustomDel,
            }}
            remarkPlugins={[remarkGfm]}
        >{processedText}</Markdown>
    )
}

const CustomStreamingUl = (props) => {
    // ** bold 변환 처리
    const children = Array.isArray(props.children) ? props.children : [props.children];

    const processedChildren = children.map((child, index) => {
        if (typeof child === 'string' && (child.match(/\*\*/g) || []).length >= 2) {
            const splitList = child.split('**');
            return splitList.map((segment, segmentIndex) => {
                if (segmentIndex % 2 === 0) {
                    return segment;
                } else {
                    return <strong key={`streaming-ul-${index}-${segmentIndex}`}>{segment}</strong>;
                }
            }).filter(Boolean);
        }
        // Handle React elements and other non-string content properly
        if (child && typeof child === 'object' && child.type) {
            return child;
        }
        // Handle string content
        if (typeof child === 'string') {
            return child;
        }
        // For any other type, convert to string safely
        return String(child || '');
    }).flat().filter(segment => segment !== '' && segment != null);

    return (
        <ul
            className={`h3 fc-gray-800 ${styles.Bubble__streamingBlock}`}
            style={{
                listStyleType: 'disc',
                paddingLeft: '0',
                whiteSpace: 'normal'
            }}
            {...props}
        >
            {processedChildren.map((child, index) =>
                typeof child === 'string' ? child : React.cloneElement(child, { key: `streaming-ul-child-${index}` })
            )}
        </ul>
    );
};

const CustomStreamingLi = (props) => {
    // ** bold 변환 처리
    const children = Array.isArray(props.children) ? props.children : [props.children];

    const processedChildren = children.map((child, index) => {
        if (typeof child === 'string' && (child.match(/\*\*/g) || []).length >= 2) {
            const splitList = child.split('**');
            return splitList.map((segment, segmentIndex) => {
                if (segmentIndex % 2 === 0) {
                    return segment;
                } else {
                    return <strong key={`streaming-li-${index}-${segmentIndex}`}>{segment}</strong>;
                }
            }).filter(Boolean);
        }
        // Handle React elements and other non-string content properly
        if (child && typeof child === 'object' && child.type) {
            return child;
        }
        // Handle string content
        if (typeof child === 'string') {
            return child;
        }
        // For any other type, convert to string safely
        return String(child || '');
    }).flat().filter(segment => segment !== '' && segment != null);

    return (
        <li
            className={`h3 fc-gray-800 ${styles.Bubble__streamingBlock}`}
            style={{ marginLeft: '20px', marginBottom: '0' }}
            {...props}
        >
            {processedChildren.map((child, index) =>
                typeof child === 'string' ? child : React.cloneElement(child, { key: `streaming-li-child-${index}` })
            )}
        </li>
    );
};

const CustomStreamingP = (props) => {
    // ** bold 변환 처리
    const children = Array.isArray(props.children) ? props.children : [props.children];
    const processedChildren = children.map((child, index) => {
        if (typeof child === 'string' && (child.match(/\*\*/g) || []).length >= 2) {
            const splitList = child.split('**');
            return splitList.map((segment, segmentIndex) => {
                if (segmentIndex % 2 === 0) {
                    return segment;
                } else {
                    return <strong key={`streaming-p-${index}-${segmentIndex}`}>{segment}</strong>;
                }
            }).filter(Boolean);
        }
        // Handle React elements and other non-string content properly
        if (child && typeof child === 'object' && child.type) {
            return child;
        }
        // Handle string content
        if (typeof child === 'string') {
            return child;
        }
        // For any other type, convert to string safely
        return String(child || '');
    }).flat().filter(segment => segment !== '' && segment != null);

    return (
        <span
            className="h3 fc-gray-800"
            style={{ whiteSpace: 'normal', margin: '0 2px 0 0' }}
            {...props}
        >
            {processedChildren.map((child, index) =>
                typeof child === 'string' ? child : React.cloneElement(child, { key: `streaming-p-child-${index}` })
            )}
        </span>
    );
};

const CustomStreamingA = (props) => {
    // 포맷: [**[:link: text]**](url) or [**[🔗 text]**](url)
    // Ensure children is always an array
    const children = Array.isArray(props.children) ? props.children : [props.children];

    const isSpecialLink = children &&
        children.some(child => {
            if (typeof child === 'string' && (child.includes(':link:') || child.includes('🔗'))) {
                return true;
            }
            // Or check if it's a React element (like <strong>) that contains :link: or 🔗
            if (child && child.props && child.props.children) {
                const strongChildren = Array.isArray(child.props.children)
                    ? child.props.children
                    : [child.props.children];

                return strongChildren.some(
                    strongChild => typeof strongChild === 'string' &&
                        (strongChild.includes(':link:') || strongChild.includes('🔗'))
                );
            }
            return false;
        });

    if (isSpecialLink) {
        return (
            <a
                {...props}
                target="_blank"
                rel="noopener noreferrer"
                className={`special-link h3 fc-gray-800 ${styles.Bubble__streamingBlock}`}
            />
        );
    } else {
        return (
            <a
                {...props}
                disabled
                style={{ cursor: 'default' }}
                className={`h3 fc-gray-800 ${styles.Bubble__streamingBlock}`}
            />
        );
    }
}

export const MarkDownStreamingConverter = (props) => {
    const { text } = props;
    return (
        <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
                ul: CustomUl,
                li: CustomLi,
                p: CustomStreamingP,
                a: CustomA,
            }}
        >{text}</Markdown>
    )
}

export default MarkDownConverter;